import { call, put, takeLatest, select } from 'redux-saga/effects';
import { message } from 'antd';
import * as types from '../constants/actionTypes';
import * as actions from '../actions/deliveryChallanActions';
import { deliveryAPI, ordersAPI } from '../../api/config';
import { fetchOrdersRequest } from '../../containers/PurchaseOrder/actions';

// ─── Saga Workers ────────────────────────────────────────────────────────────

/**
 * Fetch list of delivery challans.
 * Uses GET /api/delivery/challans (with optional detailed fallback via /api/delivery-challans).
 */
function* fetchDeliveryChallansSaga(action) {
  try {
    const response = yield call(deliveryAPI.listChallans, action.payload);
    yield put(actions.fetchDeliveryChallansSuccess(response.data));
  } catch (error) {
    const errorMessage =
      error.response?.data?.error || 'Failed to fetch delivery challans';
    yield put(actions.fetchDeliveryChallansFailure(errorMessage));
    message.error(errorMessage);
  }
}

/**
 * Fetch a single delivery challan by ID.
 * Uses GET /api/delivery-challans/:id
 */
function* fetchDeliveryChallanSaga(action) {
  try {
    const response = yield call(deliveryAPI.getChallanById, action.payload);
    yield put(actions.fetchDeliveryChallanSuccess(response.data));
  } catch (error) {
    const errorMessage =
      error.response?.data?.error || 'Failed to fetch delivery challan';
    yield put(actions.fetchDeliveryChallanFailure(errorMessage));
    message.error(errorMessage);
  }
}

/**
 * Create a delivery challan.
 * - If payload has `items` array → detailed create via POST /api/delivery-challans
 * - Otherwise → simple record via POST /api/delivery/record
 * After creation, refreshes the list and statistics automatically.
 */
function* createDeliveryChallanSaga(action) {
  // close_shortfall_items/reason are consumed here to close out short-delivered
  // lines right after the challan is created - they aren't part of the
  // delivery-challan create API's own request shape.
  const { close_shortfall_items: closeShortfallItems, close_shortfall_reason: closeShortfallReason, ...challanPayload } = action.payload;
  try {
    let response;
    if (challanPayload.items && challanPayload.items.length > 0) {
      // Detailed challan with item-level tracking (POST /api/delivery-challans)
      response = yield call(deliveryAPI.createChallanWithItems, challanPayload);
      yield put(actions.createDeliveryChallanSuccess(response.data.data || response.data));
    } else {
      // Simple delivery record (POST /api/delivery/record)
      const simplePayload = {
        purchase_order_id: challanPayload.purchase_order_id,
        challan_number: challanPayload.challan_number,
        delivery_date: challanPayload.delivery_date,
      };
      response = yield call(deliveryAPI.recordDelivery, simplePayload);
      yield put(actions.createDeliveryChallanSuccess(response.data.challan || response.data));
    }
    message.success('Delivery challan created successfully');

    // Close any lines the user flagged as "no more expected" while recording
    // this delivery, one accept-shortfall call per item, in sequence. The
    // challan itself is already created at this point, so a failure closing
    // one line is surfaced as its own toast rather than failing the saga.
    if (closeShortfallItems && closeShortfallItems.length > 0) {
      for (const item of closeShortfallItems) {
        try {
          yield call(
            ordersAPI.acceptShortfall,
            challanPayload.purchase_order_id,
            item.purchase_order_item_id,
            { reason: closeShortfallReason }
          );
        } catch (closeError) {
          message.error(
            closeError.response?.data?.details ||
              closeError.response?.data?.error ||
              `Failed to close shortfall for item ${item.purchase_order_item_id}`
          );
        }
      }
      message.success('Shortfall line(s) closed.');
      yield put(fetchOrdersRequest(1, 100));
    }

    // Refresh list and statistics so the UI is up-to-date without requiring a manual reload
    yield put(actions.fetchDeliveryChallansRequest({ page: 1, limit: 10 }));
    yield put(actions.fetchDeliveryStatisticsRequest());
  } catch (error) {
    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.details ||
      'Failed to create delivery challan';
    yield put(actions.createDeliveryChallanFailure(errorMessage));
    message.error(errorMessage);
  }
}

/**
 * Update delivery challan / status.
 * Uses POST /api/delivery/update-status
 * Response: { message, purchase_order_id, challan_number, delivery_status }
 */
function* updateDeliveryChallanSaga(action) {
  try {
    const { id, data } = action.payload;
    const payload = {
      purchase_order_id: data.purchase_order_id,
      challan_number: data.challan_number,
      delivery_status: data.delivery_status,
      delivery_date: data.delivery_date,
    };
    const response = yield call(deliveryAPI.updateDeliveryStatus, payload);
    yield put(actions.updateDeliveryChallanSuccess(response.data));
    message.success('Delivery status updated successfully');
    // Refresh the challan list so the UI reflects the new status
    const pagination = yield select((state) => state.deliveryChallans.pagination);
    yield put(actions.fetchDeliveryChallansRequest({
      page: pagination?.page || 1,
      limit: pagination?.limit || 10,
    }));
  } catch (error) {
    const errorMessage =
      error.response?.data?.error || 'Failed to update delivery challan';
    yield put(actions.updateDeliveryChallanFailure(errorMessage));
    message.error(errorMessage);
  }
}

/**
 * Verify delivery challan — reuses update-status by setting status to "delivered".
 * Uses POST /api/delivery/update-status with delivery_status = "delivered".
 */
function* verifyDeliveryChallanSaga(action) {
  try {
    const { id, data } = action.payload;
    const payload = {
      purchase_order_id: data.purchase_order_id,
      challan_number: data.challan_number,
      delivery_status: 'delivered',
      delivery_date: data.delivery_date,
    };
    const response = yield call(deliveryAPI.updateDeliveryStatus, payload);
    yield put(actions.verifyDeliveryChallanSuccess(response.data));
    message.success('Delivery challan verified successfully');
    // Refresh the challan list
    const pagination = yield select((state) => state.deliveryChallans.pagination);
    yield put(actions.fetchDeliveryChallansRequest({
      page: pagination?.page || 1,
      limit: pagination?.limit || 10,
    }));
  } catch (error) {
    const errorMessage =
      error.response?.data?.error || 'Failed to verify delivery challan';
    yield put(actions.verifyDeliveryChallanFailure(errorMessage));
    message.error(errorMessage);
  }
}

/**
 * Delete a delivery challan.
 * Uses DELETE /api/delivery/challan/:id
 */
function* deleteDeliveryChallanSaga(action) {
  try {
    yield call(deliveryAPI.deleteChallan, action.payload);
    yield put(actions.deleteDeliveryChallanSuccess(action.payload));
    message.success('Delivery challan deleted successfully');
  } catch (error) {
    const errorMessage =
      error.response?.data?.error || 'Failed to delete delivery challan';
    yield put(actions.deleteDeliveryChallanFailure(errorMessage));
    message.error(errorMessage);
  }
}

/**
 * Fetch PO delivery status.
 * Uses GET /api/purchase-orders/:id/delivery-status
 */
function* fetchPODeliveryStatusSaga(action) {
  try {
    const response = yield call(deliveryAPI.getPODeliveryStatus, action.payload);
    yield put(actions.fetchPODeliveryStatusSuccess(response.data));
  } catch (error) {
    const errorMessage =
      error.response?.data?.error || 'Failed to fetch delivery status';
    yield put(actions.fetchPODeliveryStatusFailure(errorMessage));
    message.error(errorMessage);
  }
}

/**
 * Fetch delivery status summary — reuses listChallans with filters.
 * Uses GET /api/delivery/challans
 */
function* fetchDeliveryStatusSummarySaga(action) {
  try {
    const response = yield call(deliveryAPI.listChallans, action.payload);
    // Wrap as summaries for the reducer
    const data = response.data;
    yield put(
      actions.fetchDeliveryStatusSummarySuccess({
        summaries: data.challans || [],
        pagination: data.pagination || {},
      })
    );
  } catch (error) {
    const errorMessage =
      error.response?.data?.error || 'Failed to fetch delivery summary';
    yield put(actions.fetchDeliveryStatusSummaryFailure(errorMessage));
    message.error(errorMessage);
  }
}

/**
 * Fetch delivery statistics.
 * Uses GET /api/delivery/statistics
 */
function* fetchDeliveryStatisticsSaga() {
  try {
    const response = yield call(deliveryAPI.getStatistics);
    yield put(actions.fetchDeliveryStatisticsSuccess(response.data));
  } catch (error) {
    const errorMessage =
      error.response?.data?.error || 'Failed to fetch delivery statistics';
    yield put(actions.fetchDeliveryStatisticsFailure(errorMessage));
    message.error(errorMessage);
  }
}

/**
 * Fetch challans for a single Purchase Order.
 * Uses GET /api/delivery/challans?purchase_order_id=:poId
 */
function* fetchPOChallansSaga(action) {
  try {
    const response = yield call(deliveryAPI.listChallans, {
      purchase_order_id: action.payload,
      limit: 100,
    });
    yield put(
      actions.fetchPOChallansSuccess({
        challans: response.data.challans || [],
      })
    );
  } catch (error) {
    const errorMessage =
      error.response?.data?.error || 'Failed to fetch challans for PO';
    yield put(actions.fetchPOChallansFailure(errorMessage));
    message.error(errorMessage);
  }
}

// ─── Saga Watchers ───────────────────────────────────────────────────────────

export default function* deliveryChallanSaga() {
  yield takeLatest(types.FETCH_DELIVERY_CHALLANS_REQUEST, fetchDeliveryChallansSaga);
  yield takeLatest(types.FETCH_DELIVERY_CHALLAN_REQUEST, fetchDeliveryChallanSaga);
  yield takeLatest(types.CREATE_DELIVERY_CHALLAN_REQUEST, createDeliveryChallanSaga);
  yield takeLatest(types.UPDATE_DELIVERY_CHALLAN_REQUEST, updateDeliveryChallanSaga);
  yield takeLatest(types.VERIFY_DELIVERY_CHALLAN_REQUEST, verifyDeliveryChallanSaga);
  yield takeLatest(types.DELETE_DELIVERY_CHALLAN_REQUEST, deleteDeliveryChallanSaga);
  yield takeLatest(types.FETCH_PO_DELIVERY_STATUS_REQUEST, fetchPODeliveryStatusSaga);
  yield takeLatest(types.FETCH_DELIVERY_STATUS_SUMMARY_REQUEST, fetchDeliveryStatusSummarySaga);
  yield takeLatest(types.FETCH_DELIVERY_STATISTICS_REQUEST, fetchDeliveryStatisticsSaga);
  yield takeLatest(types.FETCH_PO_CHALLANS_REQUEST, fetchPOChallansSaga);
}
